import type { TransactionParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import type { FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import type { TransactionReportFilterValue } from './TransactionReportFilter';
import {
	buildTransactionDisplayTimezone,
	buildTransactionTimeRange,
	type TransactionTimeRangeValue,
} from './TransactionTimeRangeFilter';

export type TransactionDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const addDate = (parameters: TransactionParameters, key: 'endDate', date: Dayjs | null) => {
	if (date) {
		parameters[key] = date.format('YYYY-MM-DD');
	}
};

const buildFilter = (filter: TransactionReportFilterValue): TransactionParameters['filter'] => {
	switch (filter.type) {
		case 'accountId':
			return {
				type: 'accountId',
				value: Number(filter.value),
			};

		case 'subscriptionName':
			return {
				type: 'subscriptionName',
				value: filter.value.trim(),
			};

		case 'invoiceId':
			return {
				type: 'invoiceId',
				value: filter.value.trim(),
			};

		case 'creditId':
			return {
				type: 'creditId',
				value: filter.value.trim(),
			};

		default:
			return undefined;
	}
};

export const buildTransactionReportParameters = ({
	dateRange,
	filter,
	facilityHierarchy,
	timeRange,
}: {
	dateRange: TransactionDateRangeValue;
	filter: TransactionReportFilterValue;
	facilityHierarchy: FacilityHierarchyFilterValue;
	timeRange: TransactionTimeRangeValue;
}): TransactionParameters => {
	if (!dateRange.startDate) {
		throw new Error('Select a start date.');
	}

	const parameters: TransactionParameters = {
		startDate: dateRange.startDate.format('YYYY-MM-DD'),
	};

	addDate(parameters, 'endDate', dateRange.endDate);

	const reportTimeRange = buildTransactionTimeRange(timeRange);

	if (reportTimeRange) {
		parameters.timeRange = reportTimeRange;
	}

	const displayTimezone = buildTransactionDisplayTimezone(timeRange);

	if (displayTimezone) {
		parameters.displayTimezone = displayTimezone;
	}

	const entityFilter = buildFilter(filter);

	if (entityFilter) {
		parameters.filter = entityFilter;
	}

	applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

	return parameters;
};
